import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import {
  createTask,
  deleteTask,
  listTasksForMatter,
  updateTask,
} from '../../../actions/tasks/tasks';
import { SUGGESTED_TASKS } from '../../../constants/rafSuggestedTasks';
import type {
  Task,
  TaskPriority,
  TaskReminder,
} from '../../../types/task';
import {
  REMINDER_LABELS,
  TASK_PRIORITIES,
  TASK_REMINDERS,
} from '../../../types/task';

interface TasksTabProps {
  matterId: string;
}

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  Low: 'var(--t3)',
  Medium: 'var(--teal)',
  High: 'var(--err)',
};

const isOverdue = (task: Task): boolean => {
  if (task.completed || !task.dueDate) return false;
  return task.dueDate < new Date().toISOString().slice(0, 10);
};

// Ghost input style — neutral border, focus ring in teal
const ghostInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'var(--bg)',
    fontSize: '0.84rem',
    borderRadius: '8px',
    '& fieldset': { borderColor: 'var(--border)' },
    '&:hover fieldset': { borderColor: 'var(--t3)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--teal)', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.82rem',
    color: 'var(--t3)',
    '&.Mui-focused': { color: 'var(--teal)' },
  },
};

const TasksTab: React.FC<TasksTabProps> = ({ matterId }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [reminder, setReminder] = useState<TaskReminder>('none');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listTasksForMatter(matterId);
      setTasks(result);
    } catch {
      setError('Failed to load tasks.');
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async () => {
    if (!description.trim()) return;
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const result = await createTask(matterId, {
        description: description.trim(),
        dueDate: dueDate || undefined,
        priority,
        reminder,
      });
      if (reminder === 'send_now') {
        setInfo(
          result.sentNow
            ? 'Reminder email sent now.'
            : 'Task created, but the reminder email could not be sent (no notification email configured for this organisation).',
        );
      }
      setDescription('');
      setDueDate('');
      setPriority('Medium');
      setReminder('none');
      await load();
    } catch {
      setError('Failed to add task.');
    } finally {
      setSaving(false);
    }
  };

  const toggleComplete = async (task: Task) => {
    try {
      await updateTask(matterId, task.taskId, { completed: !task.completed });
      await load();
    } catch {
      setError('Failed to update task.');
    }
  };

  const removeTask = async (task: Task) => {
    try {
      await deleteTask(matterId, task.taskId);
      await load();
    } catch {
      setError('Failed to delete task.');
    }
  };

  const pending = tasks.filter((t) => !t.completed);
  const completed = tasks.filter((t) => t.completed);

  return (
    <Box>
      {/* Add task — refined card, ghost inputs */}
      <Box
        sx={{
          bgcolor: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          p: 2.5,
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: 'var(--t1)', mb: 1.75, letterSpacing: '-0.005em' }}>
          Add Task
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr' }, gap: 1.25 }}>
          <Autocomplete
            freeSolo
            options={SUGGESTED_TASKS}
            value={description}
            onChange={(_, v) => setDescription(v || '')}
            onInputChange={(_, v) => setDescription(v)}
            size="small"
            sx={ghostInputSx}
            renderInput={(params) => (
              <TextField {...params} label="Description" placeholder="Select or type a task" />
            )}
          />
          <TextField
            label="Due date"
            type="date"
            size="small"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={ghostInputSx}
          />
          <Select
            size="small"
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            displayEmpty
            sx={{
              bgcolor: 'var(--bg)',
              fontSize: '0.84rem',
              borderRadius: '8px',
              '& fieldset': { borderColor: 'var(--border)' },
              '&:hover fieldset': { borderColor: 'var(--t3)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--teal)', borderWidth: 1.5 },
            }}
          >
            {TASK_PRIORITIES.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: '0.84rem' }}>
                {p} Priority
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={reminder}
            onChange={(e) => setReminder(e.target.value as TaskReminder)}
            sx={{
              bgcolor: 'var(--bg)',
              fontSize: '0.84rem',
              borderRadius: '8px',
              '& fieldset': { borderColor: 'var(--border)' },
              '&:hover fieldset': { borderColor: 'var(--t3)' },
              '&.Mui-focused fieldset': { borderColor: 'var(--teal)', borderWidth: 1.5 },
            }}
          >
            {TASK_REMINDERS.map((r) => (
              <MenuItem key={r} value={r} sx={{ fontSize: '0.84rem' }}>
                {REMINDER_LABELS[r]}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.75 }}>
          <Button
            variant="contained"
            size="small"
            disabled={!description.trim() || saving}
            onClick={handleAdd}
            sx={{
              bgcolor: 'var(--teal)',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.82rem',
              borderRadius: '8px',
              px: 2,
              py: 0.65,
              boxShadow: 'none',
              '&:hover': { bgcolor: 'var(--teal-mid, #2c3578)', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: 'var(--s2)', color: 'var(--t4)' },
            }}
          >
            {saving ? 'Adding...' : 'Add Task'}
          </Button>
        </Box>
        {error && (
          <Typography sx={{ color: 'var(--err)', fontSize: '0.78rem', mt: 1 }}>{error}</Typography>
        )}
        {info && (
          <Typography sx={{ color: 'var(--ok)', fontSize: '0.78rem', mt: 1 }}>{info}</Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} sx={{ color: 'var(--teal)' }} />
        </Box>
      ) : (
        <>
          <SectionHeader label="Pending" count={pending.length} />
          {pending.length === 0 && (
            <EmptyHint text="No pending tasks." />
          )}
          {pending.length > 0 && (
            <Box
              sx={{
                bgcolor: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                overflow: 'hidden',
                mb: completed.length > 0 ? 3 : 0,
              }}
            >
              {pending.map((task, i) => (
                <TaskRow
                  key={task.taskId}
                  task={task}
                  isLast={i === pending.length - 1}
                  onToggle={() => toggleComplete(task)}
                  onDelete={() => removeTask(task)}
                />
              ))}
            </Box>
          )}

          {completed.length > 0 && (
            <>
              <SectionHeader label="Completed" count={completed.length} />
              <Box
                sx={{
                  bgcolor: 'var(--bg)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}
              >
                {completed.map((task, i) => (
                  <TaskRow
                    key={task.taskId}
                    task={task}
                    isLast={i === completed.length - 1}
                    onToggle={() => toggleComplete(task)}
                    onDelete={() => removeTask(task)}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

const SectionHeader: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <Typography
    sx={{
      fontSize: '0.78rem',
      fontWeight: 600,
      color: 'var(--t2)',
      mb: 1,
      mt: 0,
      letterSpacing: '0.01em',
    }}
  >
    {label} ({count})
  </Typography>
);

const EmptyHint: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      bgcolor: 'var(--bg)',
      border: '1px dashed var(--border)',
      borderRadius: '10px',
      px: 2,
      py: 2.5,
      textAlign: 'center',
      mb: 3,
    }}
  >
    <Typography sx={{ color: 'var(--t3)', fontSize: '0.82rem' }}>
      {text}
    </Typography>
  </Box>
);

const TaskRow: React.FC<{
  task: Task;
  isLast: boolean;
  onToggle: () => void;
  onDelete: () => void;
}> = ({ task, isLast, onToggle, onDelete }) => {
  const overdue = isOverdue(task);
  const priorityColor = PRIORITY_COLORS[task.priority];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 1.75,
        py: 1.25,
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
        position: 'relative',
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'var(--s1)' },
      }}
    >
      <Checkbox
        size="small"
        checked={task.completed}
        onChange={onToggle}
        sx={{
          p: 0.5,
          color: 'var(--t3)',
          '&.Mui-checked': { color: 'var(--ok)' },
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '0.86rem',
            color: task.completed ? 'var(--t4)' : 'var(--t1)',
            textDecoration: task.completed ? 'line-through' : 'none',
            fontWeight: 500,
            lineHeight: 1.4,
          }}
        >
          {task.description}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4, flexWrap: 'wrap' }}>
          {task.dueDate && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: overdue ? 'var(--err)' : 'var(--t3)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontWeight: overdue ? 600 : 400,
              }}
            >
              Due {task.dueDate}
              {overdue && ' — overdue'}
            </Typography>
          )}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.125,
              bgcolor: `color-mix(in srgb, ${priorityColor} 10%, transparent)`,
              borderRadius: '5px',
              fontSize: '0.66rem',
              fontWeight: 600,
              color: priorityColor,
              letterSpacing: '0.02em',
            }}
          >
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: priorityColor }} />
            {task.priority}
          </Box>
          {task.reminder !== 'none' && (
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'var(--t3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.375,
              }}
            >
              <Box component="span" sx={{ fontSize: '0.78rem', lineHeight: 1 }}>⏰</Box>
              {REMINDER_LABELS[task.reminder]}
            </Typography>
          )}
        </Box>
      </Box>
      {task.completed && (
        <IconButton size="small" onClick={onToggle} title="Undo" sx={{ color: 'var(--t3)', '&:hover': { color: 'var(--t1)', bgcolor: 'var(--s2)' }, borderRadius: '6px' }}>
          <UndoIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={onDelete}
        title="Delete"
        sx={{
          color: 'var(--t3)',
          borderRadius: '6px',
          '&:hover': { color: 'var(--err)', bgcolor: 'color-mix(in srgb, var(--err) 8%, transparent)' },
        }}
      >
        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

export default TasksTab;
